import { useContext } from "react";
import { LangContext } from "./LangContext";
import { cleanText, relativeTime } from "../lib/utils";

function H2({ children }) {
  return <h2 style={{ fontSize: 16, fontWeight: 800, color: "var(--pc-fg-1-warm)", margin: "0 0 14px", letterSpacing: "-.005em" }}>{children}</h2>;
}
function Meta({ children, color }) {
  return <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".10em", textTransform: "uppercase", color: color || "var(--pc-fg-3)" }}>{children}</span>;
}
function Section({ children, last }) {
  return (
    <div style={{ padding: "20px 0" }}>
      {children}
      {!last && <div style={{ marginTop: 20, borderTop: "2px solid rgba(255,255,255,.6)" }} />}
    </div>
  );
}

function toBullets(text) {
  if (!text) return [];
  return String(text).split(/\.(?:\s|$)/).map(s => s.trim()).filter(Boolean);
}

function SourceStatusBlock({ statuses, lang }) {
  if (!statuses?.length) return null;
  return (
    <Section>
      <Meta>{lang === "en" ? "Source Collection Status" : "소스 수집 현황"}</Meta>
      <div style={{ display: "flex", flexDirection: "column", gap: 5, marginTop: 8 }}>
        {statuses.map((s, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5 }}>
            <span style={{
              width: 6, height: 6, borderRadius: 9999, flexShrink: 0,
              background: s.status === "success" ? "var(--pc-tier-notable)" : s.status === "skip" ? "var(--pc-fg-3)" : "var(--pc-dir-up)",
            }} />
            <span style={{ color: "var(--pc-fg-2)", flex: 1 }}>{s.source}</span>
            <span style={{ color: "var(--pc-fg-3)", fontSize: 11, fontFamily: "var(--pc-font-mono)" }}>
              {s.count != null ? (lang === "en" ? `${s.count} items` : `${s.count}건`) : (s.reason || "")}
            </span>
          </div>
        ))}
      </div>
    </Section>
  );
}

function SourceSummaryBlock({ summary, lang }) {
  if (!summary) return null;
  const rows = lang === "en"
    ? [
        ["Lee Ga-hyeok Live", summary.jtbc],
        ["Foreign Press",     summary.foreign],
        ["Korean Press",      summary.korean],
      ].filter(([_, v]) => v)
    : [
        ["이가혁라이브", summary.jtbc],
        ["외신",        summary.foreign],
        ["한국 언론",    summary.korean],
      ].filter(([_, v]) => v);
  return (
    <Section>
      <H2>{lang === "en" ? "Source Summary" : "소스 종합"}</H2>
      {rows.map(([label, text], i) => (
        <div key={i} style={{ marginBottom: 12 }}>
          <Meta>{label}</Meta>
          <ul style={{ marginTop: 4, paddingLeft: 0, listStyle: "none" }}>
            {toBullets(text).map((pt, j) => (
              <li key={j} style={{ display: "flex", gap: 6, fontSize: 13, color: "var(--pc-fg-2)", lineHeight: 1.6, marginBottom: 2 }}>
                <span style={{ color: "var(--pc-fg-4)" }}>·</span>
                <span style={{ flex: 1 }}>{cleanText(pt)}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
      {summary.synthesis && (
        <div style={{ marginTop: 14 }}>
          <Meta color="var(--pc-accent)">{lang === "en" ? "Synthesis" : "종합 분석"}</Meta>
          <ul style={{ marginTop: 4, paddingLeft: 0, listStyle: "none" }}>
            {toBullets(summary.synthesis).map((pt, j) => (
              <li key={j} style={{ display: "flex", gap: 6, fontSize: 13.5, color: "var(--pc-fg-1-warm)", lineHeight: 1.6, marginBottom: 2 }}>
                <span style={{ color: "var(--pc-fg-4)" }}>·</span>
                <span style={{ flex: 1 }}>{cleanText(pt)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Section>
  );
}

function KeywordsBlock({ keywords, lang }) {
  if (!keywords?.length) return null;
  return (
    <Section>
      <H2>{lang === "en" ? "Keywords" : "키워드"}</H2>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {keywords.map((kw, i) => (
          <div key={i}>
            <div style={{ fontSize: 14, fontWeight: 800, color: "var(--pc-fg-1-warm)", marginBottom: 4, letterSpacing: "-.005em" }}>
              {cleanText(kw.word)}
            </div>
            <ul style={{ paddingLeft: 0, listStyle: "none" }}>
              {toBullets(kw.description).map((pt, j) => (
                <li key={j} style={{ display: "flex", gap: 6, fontSize: 13, color: "var(--pc-fg-2)", lineHeight: 1.6, marginBottom: 1 }}>
                  <span style={{ color: "var(--pc-fg-4)" }}>·</span>
                  <span style={{ flex: 1 }}>{cleanText(pt)}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Section>
  );
}

function QuestionsBlock({ questions, lang }) {
  if (!questions?.length) return null;
  return (
    <Section>
      <H2>
        {lang === "en"
          ? `Running-mate Questions — ${questions.length}`
          : `러닝메이트 질문 ${questions.length}개`}
      </H2>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {questions.map((q, i) => (
          <div key={i} className="pc-anim-fadeUp">
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 6 }}>
              {q.id && <span style={{ fontSize: 11, fontWeight: 800, color: "var(--pc-fg-1-warm)", letterSpacing: ".05em", fontFamily: "var(--pc-font-mono)" }}>{q.id}</span>}
              {q.type && <span style={{ fontSize: 10, color: "var(--pc-fg-3)", letterSpacing: ".10em", textTransform: "uppercase", fontWeight: 700 }}>{q.type}</span>}
              {q.audience && (
                <span style={{
                  fontSize: 9, fontWeight: 800, padding: "2px 6px", letterSpacing: ".05em",
                  color: q.audience === "고관여" ? "var(--pc-tier-watershed)" : "var(--pc-fg-2)",
                  background: q.audience === "고관여" ? "rgba(232,156,86,.10)" : "var(--pc-bg-2)",
                }}>{q.audience}</span>
              )}
            </div>
            <div style={{ fontSize: 14.5, fontWeight: 700, color: "var(--pc-fg-1)", lineHeight: 1.5, letterSpacing: "-.005em" }}>
              {cleanText(q.question || q.q)}
            </div>
            {q.intent && (
              <div style={{ fontSize: 11.5, color: "var(--pc-fg-3)", lineHeight: 1.5, marginTop: 3 }}>{cleanText(q.intent)}</div>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}

function ScenariosBlock({ anchors, lang }) {
  if (!anchors?.length) return null;
  return (
    <Section last>
      <H2>{lang === "en" ? "Expected Scenarios" : "예상 시나리오"}</H2>
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        {anchors.map((a, i) => (
          <div key={i}>
            <div style={{ fontSize: 14.5, fontWeight: 700, color: "var(--pc-fg-1-warm)", marginBottom: 8, letterSpacing: "-.005em" }}>
              {cleanText(a.scenario)}
            </div>
            <div style={{ marginBottom: 10 }}>
              <Meta>{lang === "en" ? "Today's Signals" : "오늘의 신호"}</Meta>
              <div style={{ fontSize: 13, color: "var(--pc-fg-2)", lineHeight: 1.6, marginTop: 3 }}>{cleanText(a.todaySignals)}</div>
            </div>
            {a.anchorQuestions?.length > 0 && (
              <div>
                <Meta>{lang === "en" ? "Anchor Questions" : "앵커 질문"}</Meta>
                <div style={{ marginTop: 4 }}>
                  {a.anchorQuestions.map((q, j) => (
                    <div key={j} style={{
                      display: "flex", gap: 6, fontSize: 13, color: "var(--pc-fg-1-warm)",
                      fontWeight: 600, lineHeight: 1.55, marginBottom: 4,
                    }}>
                      <span style={{ color: "var(--pc-fg-4)" }}>·</span>
                      <span style={{ flex: 1 }}>{cleanText(q)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}

export default function PCAnchorTab({
  directionKeyword, onDirectionChange,
  briefing, briefingTs, collectedSources, generating, error, sourceStatuses, phase,
  onGenerate,
}) {
  const lang = useContext(LangContext);
  return (
    <div style={{ flex: 1, overflowY: "auto" }}>
      <div style={{ maxWidth: 780, margin: "0 auto", padding: "16px 24px 80px" }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          background: "var(--pc-bg-3)", border: "none",
          padding: "0 14px", height: 44, marginBottom: 8, borderRadius: 10,
        }}>
          <span style={{ color: "var(--pc-fg-3)", fontSize: 15, lineHeight: 1 }}>⌕</span>
          <input
            value={directionKeyword || ""}
            onChange={e => onDirectionChange(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !generating && onGenerate?.()}
            placeholder={lang === "en" ? "Briefing direction keyword…" : "브리핑 방향 키워드…"}
            style={{
              flex: 1, background: "transparent", border: "none", outline: "none",
              color: "var(--pc-fg-1-warm)", fontSize: 14,
              fontFamily: "var(--pc-font-sans)", fontWeight: 500, minWidth: 0,
            }}
          />
          <button onClick={() => !generating && onGenerate?.()} style={{
            background: "transparent", color: "var(--pc-fg-1-warm)", border: "none",
            padding: "6px 4px", fontSize: 11, fontWeight: 800,
            letterSpacing: ".10em", textTransform: "uppercase", cursor: "pointer", borderRadius: 0,
          }}>
            {generating
              ? (phase === "collecting"
                  ? (lang === "en" ? "Collecting…" : "수집 중…")
                  : (lang === "en" ? "Generating…" : "생성 중…"))
              : (lang === "en" ? "Generate →" : "생성 →")}
          </button>
        </div>

        {error && (
          <div style={{ marginTop: 12, color: "var(--pc-dir-up)", fontSize: 12 }}>{error}</div>
        )}

        {briefingTs && !generating && (
          <div style={{ padding: "10px 0 4px" }}>
            <Meta>{lang === "en" ? `Last briefing — ${relativeTime(briefingTs)}` : `마지막 브리핑 — ${relativeTime(briefingTs)}`}</Meta>
          </div>
        )}

        {briefing && (
          <>
            <SourceStatusBlock statuses={sourceStatuses} lang={lang} />
            <SourceSummaryBlock summary={briefing.sourceSummary} lang={lang} />
            <KeywordsBlock keywords={briefing.keywords} lang={lang} />
            <QuestionsBlock questions={briefing.questions} lang={lang} />
            <ScenariosBlock anchors={briefing.scenarioAnchors} lang={lang} />
          </>
        )}

        {!briefing && !generating && !error && (
          <div style={{ padding: 60, textAlign: "center", color: "var(--pc-fg-3)", fontSize: 12 }}>
            {lang === "en" ? "Enter a direction keyword and click Generate" : "방향 키워드를 입력하고 생성을 눌러주세요"}
          </div>
        )}
      </div>
    </div>
  );
}
